import { motion } from "framer-motion";
import { XOctagon, Ban, ShieldOff, EyeOff, CheckCircle2 } from "lucide-react";

const nevers = [
  {
    icon: XOctagon,
    title: "We never post fake positive reviews.",
    body: "Not one. Not ever. That's review fraud — and it's a fast way to get your profile suspended.",
  },
  {
    icon: Ban,
    title: "We never buy or trade reviews.",
    body: "No reciprocal review swaps. No paid stars. No incentive schemes that violate Google's Terms of Service.",
  },
  {
    icon: EyeOff,
    title: "We never gate reviews.",
    body: "Filtering happy customers to public reviews and unhappy customers to private forms is prohibited. We won't help you do it.",
  },
  {
    icon: ShieldOff,
    title: "We never use black-hat tactics.",
    body: "No bots, no impersonation, no coordinated attacks on competitors. Only Google's official reporting channels.",
  },
];

/**
 * Animated compliance seal — a rotating outer ring of text with a
 * center checkmark; conveys "signed & compliant" like a legal stamp.
 */
function ComplianceSeal() {
  return (
    <div className="relative w-32 h-32">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0"
      >
        <svg viewBox="0 0 200 200" className="w-full h-full">
          <defs>
            <path
              id="sealCircle"
              d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0"
            />
          </defs>
          <text fill="rgba(255,255,255,0.55)" fontSize="14" fontFamily="Cabinet Grotesk, ui-sans-serif" letterSpacing="6" fontWeight="700">
            <textPath href="#sealCircle" startOffset="0">
              GOOGLE COMPLIANT · POLICY VERIFIED · ETHICAL ONLY ·
            </textPath>
          </text>
        </svg>
      </motion.div>
      <div className="absolute inset-6 rounded-full border border-[#10B981]/40 bg-[#10B981]/[0.08] grid place-items-center">
        <div className="text-center">
          <CheckCircle2 className="w-8 h-8 mx-auto text-[#10B981]" />
          <div className="mt-1 font-display text-[10px] uppercase tracking-widest text-white/60 font-bold">
            Verified
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Ethics() {
  return (
    <section className="relative py-24 lg:py-32 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="text-xs uppercase tracking-[0.2em] text-white/40 font-semibold">
            The ethics line
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-[1.05]">
            What we <span className="text-[#D4AF37]">never</span> do.
          </h2>
          <p className="mt-5 text-white/55 text-sm leading-relaxed max-w-md">
            Most "reputation firms" cross a line that will eventually cost you your Google presence.
            We built Go Higher to stay on the right side of it — permanently.
          </p>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 flex items-center gap-4"
          >
            <ComplianceSeal />
            <div className="text-sm">
              <div className="font-display font-bold text-white">100% Google-compliant</div>
              <div className="text-white/50 text-xs mt-1 max-w-[180px] leading-relaxed">
                Every action follows Google's own reporting workflows and Terms of Service.
              </div>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
          {nevers.map((n, i) => (
            <motion.div
              key={n.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group rounded-2xl border border-white/10 bg-[#0d1424]/50 p-6 hover:border-[#D4AF37]/30 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 grid place-items-center rounded-xl border border-white/10 bg-white/[0.04] group-hover:border-[#D4AF37]/40 group-hover:bg-[#D4AF37]/10 group-hover:text-[#D4AF37] transition-colors">
                  <n.icon className="w-4 h-4 text-white/70 group-hover:text-[#D4AF37] transition-colors" />
                </div>
                <div>
                  <div className="font-display font-semibold text-white text-base leading-snug">
                    {n.title}
                  </div>
                  <div className="mt-1.5 text-sm text-white/50 leading-relaxed">{n.body}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
